import { ReactNode, useEffect, useState } from 'react';
import { createWeb3Modal, defaultWagmiConfig } from '@web3modal/wagmi/react';
import { WagmiConfig } from 'wagmi';
import { bsc, bscTestnet, mainnet, sepolia, avalanche, avalancheFuji, base, baseSepolia, optimism, optimismSepolia, arbitrum, arbitrumSepolia, polygon } from 'viem/chains';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useNetwork, useAccount } from 'wagmi';
import { Chain } from 'viem';

const metadata = {
  name: 'MdbApp',
  description: 'Create your own cryptocurrency token on BSC',
  url: 'https://mdbapp.io',
  icons: ['https://avatars.githubusercontent.com/u/37784886']
};

// Define Abstract chains
const abstractMainnet: Chain = {
  id: 2741,
  name: 'Abstract',
  network: 'abstract',
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://api.mainnet.abs.xyz'],
    },
    public: {
      http: ['https://api.mainnet.abs.xyz'],
    },
  },
  blockExplorers: {
    default: {
      name: 'AbScan',
      url: 'https://abscan.org',
    },
  },
};

const abstractTestnet: Chain = {
  id: 11124,
  name: 'Abstract Sepolia',
  network: 'abstract-testnet',
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://api.testnet.abs.xyz'],
    },
    public: {
      http: ['https://api.testnet.abs.xyz'],
    },
  },
  blockExplorers: {
    default: {
      name: 'AbScan',
      url: 'https://sepolia.abscan.org',
    },
  },
  testnet: true,
};

// Define Polygon Amoy testnet chain (not available in viem/chains yet)
const polygonAmoy: Chain = {
  id: 80002,
  name: 'Polygon Amoy',
  network: 'polygon-amoy',
  nativeCurrency: {
    name: 'MATIC',
    symbol: 'MATIC',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://rpc-amoy.polygon.technology'],
    },
    public: {
      http: ['https://rpc-amoy.polygon.technology'],
    },
  },
  blockExplorers: {
    default: {
      name: 'PolygonScan',
      url: 'https://amoy.polygonscan.com',
    },
  },
  testnet: true,
};

// Detect if we're on a mobile device (moved here to use in Sei chain config)
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Define Sei chains
// Mobile browsers prioritize public RPC, desktop uses Alchemy for better performance
const seiMainnet: Chain = {
  id: 1329,
  name: 'Sei',
  network: 'sei',
  nativeCurrency: {
    name: 'Sei',
    symbol: 'SEI',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: isMobile ? [
        // Mobile: prioritize public RPCs for better compatibility
        'https://evm-rpc.sei-apis.com',
        'https://sei-mainnet.g.alchemy.com/v2/fTz24O1l0KaG3202Zf9qE',
        'https://evm-rpc-arctic-1.sei-apis.com'
      ] : [
        // Desktop: prioritize Alchemy for better performance
        'https://sei-mainnet.g.alchemy.com/v2/fTz24O1l0KaG3202Zf9qE',
        'https://evm-rpc.sei-apis.com',
        'https://evm-rpc-arctic-1.sei-apis.com'
      ],
    },
    public: {
      http: [
        'https://evm-rpc.sei-apis.com',
        'https://sei-mainnet.g.alchemy.com/v2/fTz24O1l0KaG3202Zf9qE'
      ],
    },
  },
  blockExplorers: {
    default: {
      name: 'Seitrace',
      url: 'https://seitrace.com',
    },
  },
};

const seiTestnet: Chain = {
  id: 1328,
  name: 'Sei Atlantic',
  network: 'sei-testnet',
  nativeCurrency: {
    name: 'Sei',
    symbol: 'SEI',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://evm-rpc.atlantic-2.seinetwork.io'],
    },
    public: {
      http: ['https://evm-rpc.atlantic-2.seinetwork.io'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Seitrace',
      url: 'https://seitrace.com/?chain=atlantic-2',
    },
  },
  testnet: true,
};

const opbnbMainnet: Chain = {
  id: 204,
  name: 'opBNB',
  network: 'opbnb-mainnet',
  nativeCurrency: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://opbnb-mainnet-rpc.bnbchain.org'],
    },
    public: {
      http: ['https://opbnb-mainnet-rpc.bnbchain.org'],
    },
  },
  blockExplorers: {
    default: {
      name: 'opBNBScan',
      url: 'https://mainnet.opbnbscan.com',
    },
  },
};

const opbnbTestnet: Chain = {
  id: 5611,
  name: 'opBNB Testnet',
  network: 'opbnb-testnet',
  nativeCurrency: {
    name: 'tBNB',
    symbol: 'tBNB',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://opbnb-testnet-rpc.bnbchain.org'],
    },
    public: {
      http: ['https://opbnb-testnet-rpc.bnbchain.org'],
    },
  },
  blockExplorers: {
    default: {
      name: 'opBNBScan Testnet',
      url: 'https://opbnb-testnet.bscscan.com',
    },
  },
  testnet: true,
};

// Note: Base chains are imported from viem/chains
// base (8453) and baseSepolia (84532) are already defined in viem

// Configure chains with WalletConnect's infrastructure
const configuredChains = [
  bsc,
  bscTestnet,
  mainnet,
  sepolia,
  avalanche,
  avalancheFuji,
  abstractMainnet,
  abstractTestnet,
  base,
  baseSepolia,
  optimism,
  optimismSepolia,
  arbitrum,
  arbitrumSepolia,
  polygon,
  polygonAmoy,
  seiMainnet,
  seiTestnet,
  opbnbMainnet,
  opbnbTestnet
];

// Use a fallback project ID to prevent blank pages
const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '3a8170812b534d0ff9d794f19a901d64';

// Create wagmi config with WalletConnect's infrastructure
const wagmiConfig = defaultWagmiConfig({
  chains: configuredChains,
  projectId,
  metadata,
  
  // Enable WalletConnect's full infrastructure
  enableWalletConnect: true,
  enableInjected: true,
  enableEIP6963: true,
  enableCoinbase: false,
});

// Create Web3Modal with a type assertion to avoid TypeScript errors
// This is necessary because the Web3Modal types may not be fully up-to-date
createWeb3Modal({
  wagmiConfig,
  projectId,
  enableAnalytics: true,
  featuredWalletIds: [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
  ],
  excludeWalletIds: [
    '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Zerion
    'bc949c5d968ae81310268bf9193f9c9fb7bb4e1283e1284af8f2bd4992535fd6', // imToken
  ],
  termsOfServiceUrl: 'https://mdbapp.io/terms',
  privacyPolicyUrl: 'https://mdbapp.io/privacy',
  
  // Improved styling for better mobile experience
  themeVariables: {
    // Theme variables that are compatible with Web3Modal
    '--w3m-accent-color': '#0A74FF',
    '--w3m-accent-fill-color': '#FFFFFF',
    '--w3m-z-index': '1000',
    '--w3m-border-radius-master': '12px',
  } as Record<string, string>,
} as any); // Type assertion to bypass strict type checking

// Create a client with better caching and mobile optimization
// Mobile devices need more aggressive retry and longer cache times
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: isMobile, // Reconnect on mobile to handle network switches
      retry: isMobile ? 3 : 1, // More retries on mobile for flaky connections
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      staleTime: isMobile ? 15000 : 5000, // Longer stale time on mobile
      // Increase cache duration for mobile to reduce network requests
      gcTime: isMobile ? 1000 * 60 * 10 : 1000 * 60 * 5, // 10 mins for mobile, 5 mins for desktop
    },
  },
});

interface AppKitProviderProps {
  children: ReactNode;
}

// Enhanced NetworkMonitor with improved Sepolia support
function NetworkMonitor() {
  const { chain } = useNetwork();
  const { isConnected } = useAccount();
  
  // Track previous chain for change detection
  const [prevChainId, setPrevChainId] = useState<number | undefined>();
  
  useEffect(() => {
    if (!chain || !isConnected) return;
    
    console.log('NetworkMonitor: Current chain:', chain.id);
    
    // We no longer need special handling for Sepolia network
    // since we've improved gas price calculations with WalletConnect
    
    // Update previous chain ID for future change detection
    setPrevChainId(chain.id);
  }, [chain, isConnected, prevChainId]);
  
  return null;
}

function AppKitProvider({ children }: AppKitProviderProps) {
  // Use a simple wrapper to avoid type errors with WagmiConfig
  return (
    <WagmiConfig config={wagmiConfig as any}>
      <QueryClientProvider client={queryClient}>
        <NetworkMonitor />
        {children}
      </QueryClientProvider>
    </WagmiConfig>
  );
}

export default AppKitProvider;
